// src/components/AdminSettings/types.ts
import type { FamilyMember, Task, DisplaySettings, Reward, RelationshipType } from '../../types';
// Alias for backwards compatibility
type Chore = Task;
import type { AvatarValue } from '../ui/AvatarPicker';
import type { UserProfile } from '../../types/user';
import type { User } from '@supabase/supabase-js';

// Matches the Household type from AuthContext (simpler than types/household.ts)
interface AuthHousehold {
  id: string;
  name: string;
  role: string;
}

// Tab type
export type SettingsTab = 'family' | 'chores' | 'display' | 'integrations' | 'rewards' | 'appearance' | 'account' | 'photos';

// Form types
export interface ProfileFormData {
  full_name: string;
  display_name: string;
  phone: string;
  timezone: string;
}

export type MemberType = 'adult' | 'teen' | 'child' | 'pet';

export interface MemberFormData {
  name: string;
  nickname: string;
  relationship: RelationshipType | '';
  avatar: AvatarValue;
  color: string;
  is_parent: boolean;
  member_type: MemberType;
  birth_date: string | null; // ISO date string
}

// Helper to calculate member type from birth date
export function getMemberTypeFromBirthDate(birthDate: string | null): MemberType {
  if (!birthDate) return 'adult';
  const today = new Date();
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  if (age < 13) return 'child';
  if (age < 18) return 'teen';
  return 'adult';
}

// Helper to infer member type from relationship (fallback when no birth date)
export function getMemberTypeFromRelationship(relationship: RelationshipType | ''): MemberType {
  switch (relationship) {
    case 'son':
    case 'daughter':
      return 'child'; // Will be overridden by birth date if provided
    case 'father':
    case 'mother':
    case 'grandparent':
    case 'guardian':
      return 'adult';
    default:
      return 'adult';
  }
}

export type ScheduleCategory = 'morning' | 'after_school' | 'evening' | 'bedtime' | 'weekend' | 'anytime';

export interface ChoreFormData {
  name: string;
  icon: string;
  points_value: number;
  frequency: 'daily' | 'weekly' | 'monthly' | 'as_needed';
  schedule_categories: ScheduleCategory[]; // Array - tasks can belong to multiple routines
}

export interface RewardFormData {
  name: string;
  icon: string;
  points_cost: number;
}

// Props shared across tab components
export interface AdminSettingsTabProps {
  householdId: string;
}

// Account Tab Props
export interface AccountTabProps extends AdminSettingsTabProps {
  user: User | null;
  userProfile: UserProfile | null;
  currentHousehold: AuthHousehold | null;
  googleAvatarUrl: string | null;
  onEditProfile: () => void;
  onEditAvatar: () => void;
  onSignOut: () => void;
}

// Family Tab Props
export interface FamilyTabProps extends AdminSettingsTabProps {
  familyMembers: FamilyMember[];
  onAddMember: () => void;
  onEditMember: (member: FamilyMember) => void;
  onEditMemberAvatar: (member: FamilyMember) => void;
}

// Chores Tab Props
export interface ChoresTabProps extends AdminSettingsTabProps {
  chores: Chore[];
  onAddChore: () => void;
  onEditChore: (chore: Chore) => void;
  onDeleteChore: (id: string) => void;
}

// Rewards Tab Props
export interface RewardsTabProps extends AdminSettingsTabProps {
  rewards: Reward[];
  onAddReward: () => void;
  onEditReward: (reward: Reward) => void;
  onDeleteReward: (id: string) => void;
}

// Display Tab Props
export interface DisplayTabProps extends AdminSettingsTabProps {
  settings: DisplaySettings | null;
  onUpdateSettings: (updates: Partial<DisplaySettings>) => void;
}

// Appearance Tab Props
export interface AppearanceTabProps extends AdminSettingsTabProps {
  // Uses useTheme hook internally
}

// Integrations Tab Props
export interface IntegrationsTabProps extends AdminSettingsTabProps {
  googleConnected: boolean;
  haConnected: boolean;
  haUrl: string;
  haToken: string;
  haTesting: boolean;
  onHaUrlChange: (url: string) => void;
  onHaTokenChange: (token: string) => void;
  onConnectGoogle: () => void;
  onDisconnectGoogle: () => void;
  onTestAndSaveHA: () => void;
}

// Photos Tab Props
export interface PhotoAlbumWithCount {
  id: string;
  name: string;
  photo_count: number;
}

export interface PhotosTabProps extends AdminSettingsTabProps {
  albums: PhotoAlbumWithCount[];
  onAddAlbum: () => void;
  onEditAlbum: (album: PhotoAlbumWithCount) => void;
  onDeleteAlbum: (id: string) => void;
}

export interface PhotoAlbumFormData {
  name: string;
}

export interface PhotoAlbumModalProps {
  isOpen: boolean;
  onClose: () => void;
  editingAlbum: PhotoAlbumWithCount | null;
  form: PhotoAlbumFormData;
  onFormChange: (form: PhotoAlbumFormData) => void;
  onSave: () => void;
  onDelete: () => void;
}

// Modal Props
export interface EditProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  form: ProfileFormData;
  onFormChange: (form: ProfileFormData) => void;
  onSave: () => void;
}

export interface FamilyMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  editingMember: FamilyMember | null;
  form: MemberFormData;
  onFormChange: (form: MemberFormData) => void;
  onSave: () => void;
  onDelete: () => void;
  userId?: string;
  googleAvatarUrl?: string | null;
}

export interface ChoreModalProps {
  isOpen: boolean;
  onClose: () => void;
  editingChore: Chore | null;
  form: ChoreFormData;
  onFormChange: (form: ChoreFormData) => void;
  onSave: () => void;
  onDelete: () => void;
}

export interface RewardModalProps {
  isOpen: boolean;
  onClose: () => void;
  editingReward: Reward | null;
  form: RewardFormData;
  onFormChange: (form: RewardFormData) => void;
  onSave: () => void;
  onDelete: () => void;
}
